import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import {
  Tag,
  CreateTagInput,
  CreateBlockChainInput,
  ContinueTagInput,
  UpdateTagInput,
  DeleteTagInput,
  ExistsResponse,
  SuccessResponse,
  UUID,
  TagsByDocumentOptions,
  TagsByDocumentResponse,
  PaginatedTagsParams,
  PaginatedTagsResponse,
} from './types';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';
import {
  tagQueryKeys,
  invalidateOnTagCreate,
  invalidateOnBatchCreate,
  invalidateOnBlockChain,
  invalidateOnContinue,
  invalidateOnTagUpdate,
  invalidateOnTagDelete,
} from './tagCacheInvalidation';

export { tagQueryKeys };

export function useTag(tagId: UUID | undefined): UseQueryResult<Tag | null> {
  return useQuery(
    [tagQueryKeys.tag, tagId],
    async () => {
      const res = await request.get(`/gaby/tags/${tagId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as Tag) || null;
    },
    {
      enabled: !!tagId,
      onError: ServerErrorHandler,
    }
  );
}

export function useTagExists(tagId: UUID | undefined): UseQueryResult<ExistsResponse> {
  return useQuery(
    [tagQueryKeys.tagExists, tagId],
    async () => {
      const res = await request.get(`/gaby/tags/${tagId}/exists`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return { data: { exists: false } };
        }
        throw err;
      });
      return (res?.data as ExistsResponse) || { exists: false };
    },
    {
      enabled: !!tagId,
      onError: ServerErrorHandler,
    }
  );
}

export function useTagsByDocument(documentSuperId: number | undefined, options?: TagsByDocumentOptions): UseQueryResult<TagsByDocumentResponse> {
  const includeParam = options?.include?.length ? `?include=${options.include.join(',')}` : '';

  return useQuery(
    [tagQueryKeys.tagsByDocument, documentSuperId, options?.include],
    async () => {
      const res = await request.get(`/gaby/tags/document/${documentSuperId}${includeParam}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as TagsByDocumentResponse) || { tags: [], taxonomy: {} };
    },
    {
      enabled: !!documentSuperId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useTagsByElement(elementId: UUID | undefined): UseQueryResult<Tag[]> {
  return useQuery(
    [tagQueryKeys.tagsByElement, elementId],
    async () => {
      const res = await request.get(`/gaby/tags/element/${elementId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as Tag[]) || [];
    },
    {
      enabled: !!elementId,
      onError: ServerErrorHandler,
    }
  );
}

export function useTagsByDocumentPaginated(documentSuperId: number | undefined, params: PaginatedTagsParams): UseQueryResult<PaginatedTagsResponse> {
  const queryParams = new URLSearchParams();
  if (params.page != null) queryParams.set('page', String(params.page));
  if (params.pageSize != null) queryParams.set('pageSize', String(params.pageSize));
  if (params.search) queryParams.set('search', params.search);
  if (params.status?.length) queryParams.set('status', params.status.join(','));
  if (params.validationStatus?.length) queryParams.set('validationStatus', params.validationStatus.join(','));
  if (params.type?.length) queryParams.set('type', params.type.join(','));
  if (params.taxonomy?.length) queryParams.set('taxonomy', params.taxonomy.join(','));
  if (params.currency?.length) queryParams.set('currency', params.currency.join(','));
  if (params.contextId?.length) queryParams.set('contextId', params.contextId.join(','));
  if (params.include?.length) queryParams.set('include', params.include.join(','));

  const qs = queryParams.toString();
  const url = `/gaby/tags/document/${documentSuperId}/paginated${qs ? `?${qs}` : ''}`;

  return useQuery(
    [tagQueryKeys.tagsByDocumentPaginated, documentSuperId, params],
    async () => {
      const res = await request.get(url).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (
        (res?.data as PaginatedTagsResponse) || {
          rows: [],
          count: 0,
          stats: { total: 0, approved: 0, draft: 0, needsAttention: 0, invalid: 0, mandatory: 0 },
          totalMandatoryTags: 0,
        }
      );
    },
    {
      enabled: !!documentSuperId,
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}

export function useCreateTag(): UseMutationResult<Tag, unknown, CreateTagInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (tag: CreateTagInput) => {
      const res = await request.post('/gaby/tags/', tag);
      return res?.data as Tag;
    },
    {
      onError: (error: any) => {
        let errorMessage = i18n('tag.created.error');
        try {
          const responseBody = JSON.parse(error.request?.responseText);
          if (responseBody?.error) {
            errorMessage = i18n(responseBody.error);
          }
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: errorMessage,
          removeTime: 4000,
        });
      },
      onSuccess: (_data, variables) => {
        if (variables.origin !== 'ai') {
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            text: i18n(`tag.created.success`),
            removeTime: 4000,
          });
        }
        invalidateOnTagCreate(queryClient, variables);
      },
    }
  );
}

export function useBatchCreateTags(): UseMutationResult<Tag[], unknown, CreateTagInput[]> {
  const queryClient = useQueryClient();
  return useMutation(
    async (tags: CreateTagInput[]) => {
      const res = await request.post('/gaby/tags/batch', { tags });
      return res?.data as Tag[];
    },
    {
      onError: () => {
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: i18n('tag.created.error'), removeTime: 4000 });
      },
      onSuccess: (_data, tags) => {
        invalidateOnBatchCreate(queryClient, tags[0]?.documentSuperId);
      },
    }
  );
}

export function useCreateBlockChain(): UseMutationResult<Tag[], unknown, CreateBlockChainInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: CreateBlockChainInput) => {
      const res = await request.post('/gaby/tags/block-chain', input);
      return res?.data as Tag[];
    },
    {
      onError: () => {
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: i18n('tag.created.error'), removeTime: 4000 });
      },
      onSuccess: (_data, input) => {
        invalidateOnBlockChain(queryClient, input.documentSuperId);
      },
    }
  );
}

export function useContinueTag(): UseMutationResult<Tag, unknown, ContinueTagInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (tag: ContinueTagInput) => {
      const res = await request.post('/gaby/tags/continue', tag);
      return res?.data as Tag;
    },
    {
      onError: (error: any) => {
        let errorMessage = i18n('tag.created.error');
        try {
          const responseBody = JSON.parse(error.request?.responseText);
          if (responseBody?.error) {
            errorMessage = i18n(responseBody.error);
          }
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: errorMessage,
          removeTime: 4000,
        });
      },
      onSuccess: (_data, variables) => {
        if (variables.origin !== 'ai') {
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            text: i18n(`tag.created.success`),
            removeTime: 4000,
          });
        }
        invalidateOnContinue(queryClient, variables);
      },
    }
  );
}

export function useUpdateTag(): UseMutationResult<SuccessResponse, unknown, UpdateTagInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, ...tagData }: UpdateTagInput) => {
      const res = await request.put(`/gaby/tags/${id}`, tagData);
      return res?.data as SuccessResponse;
    },
    {
      onError: (error: any) => {
        let errorMessage = i18n('tag.updated.error');
        try {
          const responseBody = JSON.parse(error.request?.responseText);
          if (responseBody?.error) {
            errorMessage = i18n(responseBody.error);
          }
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: errorMessage,
          removeTime: 4000,
        });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n(`tag.updated.success`),
          removeTime: 4000,
        });
        invalidateOnTagUpdate(queryClient, variables);
      },
    }
  );
}

export function useDeleteTag(): UseMutationResult<SuccessResponse, unknown, DeleteTagInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, skipSectionRewrite }: DeleteTagInput) => {
      const res = await request.del(`/gaby/tags/${id}${skipSectionRewrite ? '?skipSectionRewrite=true' : ''}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: (error) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n('tag.deleted.error'),
          removeTime: 4000,
        });
        ServerErrorHandler(error);
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.deleted.success'),
          removeTime: 4000,
        });
        invalidateOnTagDelete(queryClient, variables);
      },
    }
  );
}
